import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, AlertCircle, CheckCircle, Info } from "lucide-react";

const ToastContext = createContext(null);

const ICONS = {
  error: AlertCircle,
  success: CheckCircle,
  info: Info,
};

const STYLES = {
  error: "bg-red-50 border-red-200 text-red-800",
  success: "bg-emerald-50 border-emerald-200 text-emerald-800",
  info: "bg-blue-50 border-blue-200 text-blue-800",
};

const ICON_STYLES = {
  error: "text-red-500",
  success: "text-emerald-500",
  info: "text-blue-500",
};

function ToastItem({ toast, onDismiss }) {
  const Icon = ICONS[toast.type] || Info;
  
  useEffect(() => {
    const timer = setTimeout(() => onDismiss(toast.id), toast.duration || 5000);
    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, onDismiss]);

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 rounded-xl border shadow-lg animate-slide-in ${STYLES[toast.type]}`}
    >
      <Icon size={20} className={`shrink-0 mt-0.5 ${ICON_STYLES[toast.type]}`} />
      <div className="flex-1 min-w-0">
        {toast.title && (
          <p className="font-semibold text-sm">{toast.title}</p>
        )}
        <p className="text-sm">{toast.message}</p>
      </div>
      <button
        onClick={() => onDismiss(toast.id)}
        className="shrink-0 p-1 hover:bg-black/5 rounded transition-colors"
      >
        <X size={16} />
      </button>
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((toast) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { ...toast, id }]);
    return id;
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showError = useCallback((message, title = "Error") => {
    return addToast({ type: "error", message, title });
  }, [addToast]);

  const showSuccess = useCallback((message, title) => {
    return addToast({ type: "success", message, title });
  }, [addToast]);

  const showInfo = useCallback((message, title) => {
    return addToast({ type: "info", message, title });
  }, [addToast]);

  return (
    <ToastContext.Provider value={{ addToast, dismissToast, showError, showSuccess, showInfo }}>
      {children}
      {createPortal(
        <div className="fixed top-4 right-4 z-[99999] flex flex-col gap-2 max-w-sm w-full pointer-events-none">
          {toasts.map((toast) => (
            <div key={toast.id} className="pointer-events-auto">
              <ToastItem toast={toast} onDismiss={dismissToast} />
            </div>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}
